import {
    Alignment, ApiMetadataRetriever, Color, CurrencySettings, CurrencyUtil, DateUtil, DecimalFormat, DisplayType, DisplayValue,
    DOMUtil, ExtendedDateFormat, GeneralSettings, getRelativeDateString, getThemeColor, HorizontalAlignment,
    isRightAlignedDisplayType, LayoutNameResolver, LeftOrRightAlignment, MixinUtil, ModelRow, Navigation, StringUtil,
    VerticalAlignment
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { toDate } from "date-fns-tz";
import { BusyMixin } from "../../mixins/BusyMixin";
import { BusyMixinInterface } from "../../mixins/BusyMixinInterface";
import { Component } from "../../base/Component";
import { ComponentListenerDefs } from "../../base/ComponentListenerDefs";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { CurrencyExchangeInfoTooltipSource } from "../../page/tooltips/CurrencyExchangeInfoTooltipSource";
import { ListenerListDef } from "../../base/ListenerListDef";
import { QuickInfoMixin } from "../../mixins/QuickInfoMixin";
import { QuickInfoMixinInterface } from "../../mixins/QuickInfoMixinInterface";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { Image } from "../../components/image/Image";
import { DomEvent } from "../../events/DomEvent";
import { ImageProps } from "../image/ImageProps";
import { LabelPropDefinitions, LabelProps } from "./LabelProps";
import { LabelVariant, LabelVariantUtil } from "./LabelVariant";
import { LabelStyles } from "./LabelStyles";
import { ListLayoutLauncher, ListLayoutLauncherProps } from "../../page/ListLayoutLauncher";
import { ReadMoreType } from "./ReadMoreType";
import { Container } from "../../base/Container";
import { LabelShapeType } from "./LabelShapeType";
import { LabelShape } from "./LabelShape";
import { DeserializeProps } from "../../serializer/ComponentDeserializer";
import { DesignerStyleConstants } from "../../base/DesignerStyles";
import { TooltipContent } from "../../page/tooltips/TooltipContent";
import { TooltipSource } from "../../page/tooltips/TooltipSource";
import { TooltipSourceType } from "../../page/tooltips/TooltipSourceType";

export class Label extends Component implements LabelProps {
    private _allowSelect: boolean;
    private _text: string;
    private _collapseVerticallyWhenEmpty: boolean;
    private _explicitDisplayType: DisplayType;
    private _format: string;
    private _image: Image;
    private _imageName: ImageName;
    private _imageWidth: number;
    private _imageHeight: number;
    private _imageRotation: number;
    private _imageProps: Partial<ImageProps>;
    private _imageAlign: LeftOrRightAlignment;
    private _imageMarginLeft: number
    private _imageMarginRight: number
    private _imageColor: Color;
    private _imageStroke: Color;
    private _imageStrokeWidth: number;
    private _imageTransition: string;
    private _listLayout: string;
    private _link: string;
    private _linkHardRefresh: boolean;
    private _linkOpenInNewTab: boolean;
    private _linkToListLayout: boolean;
    private _verticalAlign: VerticalAlignment;
    private _wrap: boolean;
    private _readMoreDiv: HTMLSpanElement;
    private _readMoreLabel: Label;
    private _readMoreType: ReadMoreType;
    private _preReadMoreMaxHeight: string | number;
    private _readMoreCallback: () => void;
    private _textNode: Text;
    private _nullDisplayValue: string;
    private busySpinner: Image;
    private _busySpinnerAlignment: LeftOrRightAlignment
    private _scrollX: boolean;
    private _scrollY: boolean;
    private _shape: LabelShapeType;
    private designerWrapper: HTMLElement;
    private _variant: LabelVariant;

    constructor(props?: Partial<LabelProps>) {
        super("div", props);
        this._textNode = document.createTextNode("");
        this._element.appendChild(this._textNode);
        this.addClass("cmp-base");
        this.addClass(LabelStyles.base);
        this.setProps(props);
    }

    override setProps(props: Partial<LabelProps>) {
        if (props?.variant != null) {
            this.variant = props.variant;
            delete props.variant;
        }
        if (props?.shape != null) {
            this.shape = props.shape;
            delete props.shape;
        }
        super.setProps(props);
    }

    override async _deserializeSpecialProps(props: DeserializeProps): Promise<string[]> {
        const result = await super._deserializeSpecialProps(props);
        if (props.def?.shape) {
            // set the shape first so the shape's associated props can be overridden
            this.shape = props.def.shape;
            result.push("shape");
        }
        return result;
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (this.field != null) {
            const value = data?.get(this.field);
            if (value != null) {
                if (this.displayType === DisplayType.DATETIME && this.format === ExtendedDateFormat.RELATIVE) {
                    //https://github.com/marnusw/date-fns-tz#time-zone-helpers
                    const dateInClientTz = toDate(value + " " + GeneralSettings.get().server_tz_id);
                    this.text = getRelativeDateString(dateInClientTz, { object: this, propertyToSet: "text" });
                    this.tooltipDynamicContent = DateUtil.formatDateTime(dateInClientTz, DateUtil.getUserDateTimeFormat());
                }
                else if (this.displayType === DisplayType.STRING && this.format != null)
                    this.text = DisplayValue.getFormattedDataString(this.format, data.getFirstLookupModelData(this.field, data));
                else if (this.displayType === DisplayType.CURRENCY) {
                    this.text = CurrencyUtil.formatCurrency(data.get(this.field))
                    if (CurrencySettings.getSingleton().colorNegatives() === true) {
                        if (parseFloat(CurrencyUtil.removeFormatting(this.text)) < 0)
                            this.addClass(LabelStyles.negativeCurrency)
                        else
                            this.removeClass(LabelStyles.negativeCurrency)
                    }
                }
                else if (this.displayType === DisplayType.TEMPERATURE) {
                    this.text = `${data.get(this.field)}\u00B0`
                }
                else if (this.displayType === DisplayType.PERCENTAGE) {
                    this.text = `${data.get(this.field)}%`
                }
                else {
                    const metadataField = this._boundField != null ? this._boundField : this.dataSource?.getMetadataFromCache()?.output[this.field];
                    const dbDisplay = metadataField?.getDisplayValue(value);
                    if (dbDisplay != null)
                        this.text = dbDisplay;
                    else
                        this.text = DisplayValue.getDisplayValue(value, this.displayType, this.format);
                }
            }
            else
                this.text = null; //null out hard-coded text since there was no value in the bound field
        }
        if (this.nullDisplayValue == "hide") {
            this.visible = !StringUtil.isEmptyString(this.text);
        }
        else if (this.text == null) {
            this.text = this.nullDisplayValue;
        }
    }

    public get shape(): LabelShapeType {
        return this._shape;
    }

    public set shape(value: LabelShapeType) {
        if (this._shape === value)
            return;
        const oldValue = this._shape;
        this._shape = value;
        this.syncShapeProps(this._shape, oldValue);
    }

    private syncShapeProps(newValue: LabelShapeType = this._shape, oldValue?: LabelShapeType) {
        if (oldValue != null) {
            LabelShape.get(oldValue)?.removeProps(this);
        }
        LabelShape.get(newValue)?.applyProps(this);
        this.syncDesignerWrapper();
        this.syncTextContentInDesigner();
    }

    private getLabelShape(): LabelShape {
        return LabelShape.get(this._shape);
    }

    // When a shape is applied, the clip-path may cut off the designer border.
    // This method ensures the designer border is visible by creating a wrapper div and applying the borders to it.
    private syncDesignerWrapper() {
        if (this._designer == null)
            return;

        if (this._shape != null) {
            this.addMountListener(() => this.createDesignerWrapper());
        } else {
            this.removeDesignerWrapper();
        }
    }

    private createDesignerWrapper() {
        if (this._shape == null || (this.designerWrapper && DOMUtil.isOrContains(this.designerWrapper, this._element)))
            return;
        const newWrapper = document.createElement("div");
        if (this.designerWrapper != null) {
            DOMUtil.copyClasses(this.designerWrapper, newWrapper, (name) => DesignerStyleConstants.isDesignerStyle(name), true);
            this.designerWrapper.remove();
        }
        this.designerWrapper = newWrapper;
        this.designerWrapper.style.backgroundColor = "transparent";
        this.designerWrapper.id = this._element.id + "-wrapper";
        DOMUtil.copyClasses(this._element, this.designerWrapper, (name) => DesignerStyleConstants.isDesignerStyle(name), true);
        DOMUtil.replaceElement(this.designerWrapper, this._element);
        this.designerWrapper.appendChild(this._element);
    }

    private removeDesignerWrapper() {
        if (this.designerWrapper == null)
            return;
        DOMUtil.replaceElement(this._element, this.designerWrapper);
        DOMUtil.copyClasses(this.designerWrapper, this._element, (name) => DesignerStyleConstants.isDesignerStyle(name), true);
        this.designerWrapper = null;
    }

    override internalSetClassIncluded(className: string, included: boolean = true, element: HTMLElement = this._element) {
        if (this.designerWrapper != null && DesignerStyleConstants.isDesignerStyle(className))
            element = this.designerWrapper;
        super.internalSetClassIncluded(className, included, element);
    }

    get parent(): Container {
        return super.parent;
    }

    set parent(value: Container) {
        super.parent = value;
        this.syncDesignerWrapper();
    }

    override setDesigner(designer: any) {
        super.setDesigner(designer);
        this.syncAllowSelect();
    }

    public override propChangedInDesigner(propName: string, oldValue: any, newValue: any) {
        if (this.shape != null && propName != "shape" && newValue == null) {
            this.getLabelShape()?.syncPropIfNeeded(this, propName);
        }
    }

    get text(): string {
        return this._text;
    }

    set text(value: string) {
        if (this._designer == null || this.field == null) {
            const oldValue = this._text;
            if (oldValue !== value) {
               this._text = value;
                this.syncText(oldValue);
                this._setDefaultImageMargins();
            }
        }
    }

    private syncText(oldValue: string) {
        const value = this._text;
        this.syncLink();

        this._setTextNodeValue(value);
        this._evaluateReadMore();

        this._showDesignerBorderIfEmpty();
        this._matchIdToValue(oldValue, value);
    }

    private _setTextNodeValue(value: string) {
        if (!this.collapseVerticallyWhenEmpty && StringUtil.isEmptyString(value)) {
            this._element.style.display = "block";
            value = " ";
        } else {
            if (!this.collapseVerticallyWhenEmpty && this._element.style.display === "block")
                this._element.style.display = "";

            if (value == null)
                value = "";
        }
        this._textNode.textContent = value;
    }

    override getFocusTarget(): HTMLElement {
        return undefined;
    }

    get maxHeight(): string | number {
        return super.maxHeight;
    }

    set maxHeight(value: string | number) {
        if (value != null) {
            this._preReadMoreMaxHeight = value;
        }
        super.maxHeight = value;
        if (!this.shape && ((typeof value === "string" && DOMUtil.convertStyleAttrToNumber(value) !== 0) || value !== 0)) {
            this._element.style.alignItems = "flex-start";
        }
        else {
            this._element.style.alignItems = "center";
        }
        this._evaluateReadMore();
    }

    get readMoreType(): ReadMoreType {
        return this._readMoreType == null ? ReadMoreType.NONE : this._readMoreType;
    }

    set readMoreType(value: ReadMoreType) {
        if (value === this._readMoreType)
            return;
        this._readMoreType = value;
        this.addMountListener(() => this._evaluateReadMore());
    }

    private _determineReadMoreAction(): (() => void) {
        switch (this.readMoreType) {
            case ReadMoreType.INCREASE_HEIGHT:
                this._toggleReadMore();
                break;
            case ReadMoreType.SHOW_IN_DIALOG:
                this._showReadMoreInDialog();
                break;
            default:
                return null;
        }
    }

    private _toggleReadMore() {
        const curr = this._element.style.maxHeight;
        if (curr == null || curr === "")
            this.maxHeight = this._preReadMoreMaxHeight;
        else
            this.maxHeight = null;

        if (this._readMoreCallback != null)
            this._readMoreCallback();
    }

    get readMoreCallback(): () => void {
        return this._readMoreCallback;
    }

    set readMoreCallback(fn: () => void) {
        this._readMoreCallback = fn;
    }

    private _showReadMoreInDialog() {
        ReflectiveDialogs.showDialog(this.text);
    }

    private _evaluateReadMore() {
        if (this.readMoreType === ReadMoreType.NONE)
            return;
        // NOTE: calling element.clientHeight and scrollHeight will cause the DOM to reflow,
        // so if this Label is mounted and this is called repeatedly, this could cause performance problems
        if (!StringUtil.isEmptyString(this.text) && this._element.clientHeight < this._element.scrollHeight) {
            this._element.style.display = "block";
            this._readMoreDiv = document.createElement("span");
            this._readMoreDiv.style.position = "absolute";
            this._readMoreDiv.style.bottom = "0px";
            this._readMoreDiv.style.margin = "0px";
            this._readMoreDiv.style.right = "0px";
            this._readMoreDiv.style.width = "100%";
            this._readMoreDiv.style.backgroundColor = DOMUtil.getStyleValueFromElementOrAncestor("backgroundColor", this._element, getThemeColor("defaultBackground"));
            if (this._readMoreLabel == null)
                this._readMoreLabel = new Label({
                    fontSize: this.fontSize,
                    color: "primary",
                    onClick: (event) => this._determineReadMoreAction(),
                    margin: 0,
                    padding: 0,
                    align: HorizontalAlignment.CENTER
                });
            this._readMoreLabel.text = "Read More";
            this._readMoreDiv.appendChild(this._readMoreLabel.element);
            this._element.appendChild(this._readMoreDiv);
        }
        else if (this._readMoreLabel != null) {
            if (this._readMoreDiv != null) {
                this._readMoreDiv.removeChild(this._readMoreLabel.element);
                this._element.removeChild(this._readMoreDiv);
                this._readMoreDiv = null;
            }
            this._readMoreLabel.text = "Read Less";
            // this._element.parentElement.insertBefore(this._readMoreLabel.element, this._element.nextSibling);
            this._element.appendChild(this._readMoreLabel.element);
        }
    }

    public get collapseVerticallyWhenEmpty() {
        return this._collapseVerticallyWhenEmpty;
    }

    public set collapseVerticallyWhenEmpty(value: boolean) {
        this._collapseVerticallyWhenEmpty = value;
        this.syncText(this.text);
    }

    get align(): HorizontalAlignment {
        if (super.align === undefined && isRightAlignedDisplayType(this.displayType))
            return HorizontalAlignment.RIGHT;
        return super.align || HorizontalAlignment.LEFT;
    }

    set align(value: HorizontalAlignment) {
        super.align = value;
        this._syncAlign();
    }

    get allowSelect() {
        return this._allowSelect == null ? true : this._allowSelect;
    }

    set allowSelect(value) {
        this._allowSelect = value;
        this.syncAllowSelect();
    }

    private syncAllowSelect() {
        this.setClassIncluded(LabelStyles.noSelect, !this.allowSelect || this._designer != null);
    }

    get verticalAlign(): VerticalAlignment {
        return this._verticalAlign;
    }

    set verticalAlign(value: VerticalAlignment) {
        this._verticalAlign = value;
        if (value === VerticalAlignment.TOP)
            this._element.style.alignSelf = "flex-start";
        else if (value === VerticalAlignment.BOTTOM)
            this._element.style.alignSelf = "flex-end";
        else
            this._element.style.alignSelf = "";
    }

    override get field(): string {
        return super.field;
    }

    override set field(value: string) {
        super.field = value;
        this.syncTextContentInDesigner();
        this._showDesignerBorderIfEmpty();
    }

    syncTextContentInDesigner() {
        if (this._designer != null) {
            if (this.field != null && this.text != null)
                this.text = null;

            if (this.field?.length > 3 && this.shape) {
                this._textNode.textContent = this.field.substring(0, 3);
            } else {
                this._textNode.textContent = this.field ?? this.text;
            }
        }
    }

    get format(): string {
        return this._format ?? this.defaultFormat;
    }

    set format(value: string) {
        this._format = value;
    }

    private get defaultFormat(): string {
        if (this.displayType === DisplayType.DECIMAL) {
            return DecimalFormat.createPatternFromScale(this._boundField?.scale);
        }
        return null;
    }

    get displayType(): DisplayType {
        let result = this._explicitDisplayType;
        if (result === undefined && this._boundField != null)
            result = this._boundField.displayType;
        return result ?? DisplayType.STRING;
    }

    set displayType(value: DisplayType) {
        this._explicitDisplayType = value;
        if (this.displayType === DisplayType.LINK) {
            const hasRealText = this.hasNonNullDisplayValue();
            const hasLink = !StringUtil.isEmptyString(this.link);
            if (!hasRealText && hasLink) {
                this.text = this.link;
            }
        }
        this.syncLink();
        this._syncAlign();
    }

    private isLinkDisplayType(): boolean {
        return this.displayType === DisplayType.EMAIL ||
            this.displayType === DisplayType.LINK ||
            this.displayType === DisplayType.PHONE;
    }

    private _syncAlign() {
        const value = this.align;
        let styleValue = "";
        if (value === HorizontalAlignment.RIGHT)
            styleValue = "right";
        else if (value === HorizontalAlignment.CENTER)
            styleValue = "center";
        this.style.justifyContent = styleValue; // if display is flex, it will use this style
        this.style.textAlign = styleValue; // if display is block, it will use this style
    }

    protected override _fieldBindingChanged(): void {
        super._fieldBindingChanged();
        if (this._boundField?.lookupModel != null) {
            ApiMetadataRetriever.get(this._boundField?.lookupModel);
            this.syncTooltipMouseListeners();
        }
        this._syncAlign();
        this.syncTooltipMouseListeners();
        this.syncContextMenuListeners();
    }

    get link() {
        return this._link;
    }

    set link(value) {
        this._link = value;
        if (this.displayType === DisplayType.LINK && StringUtil.isEmptyString(this.text) &&
            !StringUtil.isEmptyString(this.link)) {
            this.text = this.link;
        }
        this.syncLinkClickListener();
    }

    private syncLinkClickListener() {
        const linkPresent = !StringUtil.isEmptyString(this.link) || this.canLinkToListLayout ;
        (linkPresent) ?
            this.addClickListener(this.handleLinkClick) :
            this.removeClickListener(this.handleLinkClick);
        this.applyLinkStyle(linkPresent);
    }

    get linkHardRefresh(): boolean {
        return this._linkHardRefresh;
    }

    set linkHardRefresh(value: boolean) {
        this._linkHardRefresh = value;
    }

    get linkOpenInNewTab(): boolean {
        return this._linkOpenInNewTab;
    }

    set linkOpenInNewTab(value: boolean) {
        this._linkOpenInNewTab = value;
    }

    private syncLink() {
        if (this.linkToListLayout) {
            this.syncLinkClickListener();
            return;
        }

        if (!this.isLinkDisplayType()) {
            return;
        }
        const hasRealText = this.hasNonNullDisplayValue();
        const hasLink = !StringUtil.isEmptyString(this.link);
        switch (this.displayType) {
            case DisplayType.LINK:
                // If the label is displaying a link and text has been provided, but no link value has been provided,
                // assume the text is the link
                if (!hasLink && hasRealText) {
                    this.link = this.text;
                }
                break;
            case DisplayType.EMAIL:
                // If the label is displaying an email, the text is assumed to be the email address
                this.link = hasRealText ? (`mailto:${this.text}`) : null;
                break;
            case DisplayType.PHONE:
                // If the label is displaying a phone number, the text is assumed to be the phone number
                this.link = hasRealText ? (`tel:${this.text}`) : null;
                if (hasRealText) {
                    this.tooltip = this.tooltip ? this.tooltip : "Click to dial";
                } else if (this.tooltip === "Click to dial") {
                    this.tooltip = null;
                }
                break;
            default:
                break;
        }
    }

    public applyLinkStyle(included: boolean = true) {
        this.setClassIncluded(LabelStyles.link, included);
    }

    public applyButtonHoverStyle(included: boolean = true) {
        this.setClassIncluded(LabelStyles.buttonHover, included);
    }

    handleLinkClick = () => {
        if (this.canLinkToListLayout ) {
            ListLayoutLauncher.open({...this.listLayoutLauncherProps });
            return;
        }
        const row = this?.dataSource?.activeRow;
        const link = DisplayValue.getFormattedDataString(this.link, row, null, true);
        if (link.startsWith("mailto:") || link.startsWith("tel:")) {
            window.location.href = this.link;
        } else if (link.indexOf("://") >= 0) {
            window.open(link, "_blank");
        } else {
            Navigation.navigateTo(link, {
                newTab: this.linkOpenInNewTab,
                hardRefresh: this.linkHardRefresh
            });
        }
    }

    get wrap() {
        return this._wrap == null ? true : this._wrap;
    }

    set wrap(value) {
        this._wrap = value;
        if (value) {
            this._element.style.wordBreak = "break-word";
            this._element.style.whiteSpace = "pre-wrap";
            this._element.style.display = "";
        }
        else {
            this._element.style.wordBreak = "";
            this._element.style.whiteSpace = "nowrap";
            this._element.style.display = "inline-block";
        }
    }

    get imageName(): ImageName {
        return this._imageName;
    }

    set imageName(value: ImageName) {
        this._imageName = value;
        this._imageUpdated();
    }

    get imageColor(): Color {
        return this._imageColor;
    }

    set imageColor(value: Color) {
        this._imageColor = value;
        this._imageUpdated();
    }

    get imageWidth(): number {
        return this._imageWidth;
    }

    set imageWidth(value: number) {
        this._imageWidth = value;
        this._imageUpdated();
    }

    get imageHeight(): number {
        return this._imageHeight;
    }

    set imageHeight(value: number) {
        this._imageHeight = value;
        this._imageUpdated();
    }

    get imageRotation(): number {
        return this._imageRotation;
    }

    set imageRotation(value: number) {
        this._imageRotation = value;
        this._imageUpdated();
    }

    public rotateWithTransition(rotation: number, speed: number) {
        this._imageTransition = Image.resolveTransitionValue(speed);
        this.imageRotation = rotation;
    }

    get imageStroke(): Color {
        return this._imageStroke;
    }

    set imageStroke(value: Color) {
        this._imageStroke = value;
        this._imageUpdated();
    }

    get imageStrokeWidth(): number {
        return this._imageStrokeWidth;
    }

    set imageStrokeWidth(value: number) {
        this._imageStrokeWidth = value;
        this._imageUpdated();
    }

    get imageTransition(): string {
        return this._imageTransition;
    }

    set imageTransition(value: string) {
        if (this.imageTransition === value) {
            return;
        }
        this._imageTransition = value;
        this._imageUpdated();
    }

    get imageAlign() {
        return this._imageAlign ?? this.getPropertyDefinitions().imageAlign.defaultValue;
    }

    set imageAlign(value: LeftOrRightAlignment) {
        if (value === this._imageAlign)
            return;
        this._imageAlign = value;
        this.placeImageElement(this._image, this.imageAlign);
    }

    get imageProps(): Partial<ImageProps> {
        return this._imageProps;
    }

    set imageProps(value: Partial<ImageProps>) {
        this._imageProps = value;
        this._imageUpdated();
    }

    get imageMarginRight(): number {
        return this._imageMarginRight;
    }

    set imageMarginRight(value: number) {
        this._imageMarginRight = value;
        this._imageUpdated();
    }

    get imageMarginLeft(): number {
        return this._imageMarginLeft;
    }

    set imageMarginLeft(value: number) {
        this._imageMarginLeft = value;
        this._imageUpdated();
    }

    get nullDisplayValue(): string {
        return this._nullDisplayValue;
    }

    set nullDisplayValue(value: string) {
        this._nullDisplayValue = value;
    }

    _imageUpdated() {
        if (this._imageName == null && this._imageProps?.name == null) {
            if (this._image != null && this._element.contains(this._image._element))
                this._element.removeChild(this._image._element);
            this._image = null;
        }
        else {
            const props = {
                name: this._imageName,
                color: this._imageColor,
                transition: this._imageTransition,
                rotation: this._imageRotation,
                id: this.createInnerComponentId("image"),
                ...this.imageProps
            };
            if (this._imageWidth !== undefined) {
                props.width = this._imageWidth;
            }
            if (this._imageHeight !== undefined) {
                props.height = this._imageHeight;
            }
            if (this._imageStroke !== undefined) {
                props.stroke = this._imageStroke;
            }
            if (this._imageStrokeWidth !== undefined) {
                props.strokeWidth = this._imageStrokeWidth;
            }
            if (this._image == null) {
                this._image = ComponentTypes.createComponentOfType("image", props);
                this.placeImageElement(this._image, this.imageAlign);
            }
            else
                this._image.setProps(props);
            this._setDefaultImageMargins();
        }
        this._showDesignerBorderIfEmpty();
    }

    private placeImageElement(image: Image, alignment: LeftOrRightAlignment) {
        if (image != null) {
            if (image._element.parentElement === this._element)
                this._element.removeChild(image._element);
            if (alignment !== Alignment.RIGHT) //default to left-aligned
                this._element.prepend(image._element);
            else
                this._element.append(image._element);
        }
    }

    private _setDefaultImageMargins() {
        if (this._image == null || this.text == null)
            return;
        if (this._image.marginLeft == null)
            this._image.marginLeft = this._imageMarginLeft == null ? 4 : this._imageMarginLeft;
        if (this._image.marginRight == null)
            this._image.marginRight = this._imageMarginRight == null ? 4 : this._imageMarginRight;
    }

    _showDesignerBorderIfEmpty() {
        if (this._designer != null) {
            if (this.isEmpty() && StringUtil.isEmptyString(this._textNode.textContent))
                this._element.classList.add(LabelStyles.designerEmpty);
            else
                this._element.classList.remove(LabelStyles.designerEmpty);
        }
    }

    protected applyBusy() {
        this.busy === true ? this.displayBusySpinner() : this.hideBusySpinner();
    }

    private displayBusySpinner() {
        this.hideBusySpinner();
        this.busySpinner = this.createBusySpinner();

        // If the Label already displays an image, replace that image with the busy spinner (so that the Label doesn't
        // change size).  Otherwise, display the busy spinner based on the busySpinnerAlignment.
        if (this._image?._element.parentElement === this._element)
            this._element.replaceChild(this.busySpinner._element, this._image._element);
        else
            this.placeImageElement(this.busySpinner, this.busySpinnerAlignment);
    }

    private hideBusySpinner() {
        if (this.busySpinner == null) {
            return;
        }
        // If the Label has an image to display, replace the busy spinner with that image.
        // Otherwise, remove the busy spinner.
        if (this._image != null)
            this._element.replaceChild(this._image._element, this.busySpinner._element);
        else
            this._element.removeChild(this.busySpinner._element);
        this.busySpinner = null;
    }

    private createBusySpinner(): Image {
        const labelHeight = DOMUtil.getElementHeight(this._element, {includeMargin:true});
        // Fit the busy spinner image to the current size of the label, so that the label doesn't
        // change height when busy.
        return new Image({
            id: "label" + this.id + "-busySpinner",
            name: ImageName.spinner,
            rotate: true,
            color: this.color,
            height: labelHeight - 8,
            width: labelHeight - 8,
            marginLeft: 4,
            marginRight: 4
        });
    }

    public get busySpinnerAlignment(): LeftOrRightAlignment {
        return this._busySpinnerAlignment || this.getPropertyDefinitions().busySpinnerAlignment.defaultValue;
    }

    public set busySpinnerAlignment(value: LeftOrRightAlignment) {
        if (this._busySpinnerAlignment === value) {
            return;
        }
        this._busySpinnerAlignment = value;
        if (this.busy) {
            this.displayBusySpinner();
        }
    }

    protected _getDefaultEventProp(): string {
        return "onDataDisplay";
    }

    override getPropertyDefinitions() {
        return LabelPropDefinitions.getDefinitions();
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        const shapeDefault = this.getLabelShape()?.labelProps[prop.name];
        if (shapeDefault != null) {
            return shapeDefault;
        } else if (prop.name === "displayType") {
            return this._boundField?.displayType || DisplayType.STRING;
        } else if (prop.name === "align") {
            return isRightAlignedDisplayType(this.displayType)
            ? HorizontalAlignment.RIGHT
            : HorizontalAlignment.LEFT;
        } else if (prop.name === "wrap") {
            return true;
        } else if (prop.name === "quickInfoLayout") {
            return this.getQuickInfoLayoutDefaultValue();
        } else if (prop.name === "listLayout") {
            return this._boundField?.listLayout;
        } else if (prop.name === "format") {
            return this.defaultFormat;
        }
        return super.determinePropertyDefaultValue(prop);
    }

    protected override isPropSet(propName: string): boolean {
        if (propName === "allowSelect")
            return this._allowSelect != null;
        return super.isPropSet(propName);
    }

    override getSearchValues(): string[] {
        const result = [];
        result.push(this.text);
        return result;
    }

    override get serializationName() {
        return "label";
    }

    override get properName(): string {
        return "Label";
    }

    override getBasicValue(): any {
        return this.text;
    }

    override domEventFiringListeners(event: DomEvent, eventDef: ListenerListDef): boolean {
        if (eventDef === ComponentListenerDefs.click && this.busy)
            return false;
        return super.domEventFiringListeners(event, eventDef)
    }

    public override isEmpty(): boolean {
        return StringUtil.isEmptyString(this.text) && this.imageName == null;
    }

    public hasText(): boolean {
        return StringUtil.isEmptyString(this.text) === false;
    }

    public hasNullDisplayValue(): boolean {
        return this.text === this.nullDisplayValue;
    }

    public hasNonNullDisplayValue(): boolean {
        return this.hasText() && !this.hasNullDisplayValue();
    }

    public get scrollX(): boolean {
        return this._scrollX;
    }

    public set scrollX(value: boolean) {
        this._scrollX = value;
        this.style.overflowX = value ? "auto" : "";
    }

    public get scrollY(): boolean {
        return this._scrollY;
    }

    public set scrollY(value: boolean) {
        this._scrollY = value;
        this.style.overflowY = value ? "auto" : "";
    }

    override _applyEnabled(value: boolean): void {
        this.commonApplyEnabled(value);
    }

    override get tooltipSources(): TooltipSource[] {
        const result = super.tooltipSources;
        if (this._designer == null) {
            result.push(this.quickInfoTooltipSource);
            const currencyExchangeInfoSource = this.currencyExchangeInfoTooltipSource;
            if (currencyExchangeInfoSource != null) {
                result.push(currencyExchangeInfoSource);
            }
        }
        return result;
    }

    protected override get dynamicTooltipSource(): TooltipSource {
        const result = super.dynamicTooltipSource;
        return result;
    }

    private get quickInfoTooltipSource(): TooltipSource {
        const result = new TooltipSource(TooltipSourceType.QUICK_INFO, this.quickInfoTooltipContent);
        return result;
    }

    private get quickInfoTooltipContent(): TooltipContent {
        return this.quickInfoLayout != null ? this.loadedQuickInfoLayout : null;
    }

    private get currencyExchangeInfoTooltipSource(): CurrencyExchangeInfoTooltipSource {
        if (this.displayType === DisplayType.CURRENCY && CurrencySettings.getSingleton()?.multiCurrencyEnabled()) {
            return new CurrencyExchangeInfoTooltipSource(this);
        }
        return null;
    }

    public get variant(): LabelVariant {
        return this._variant;
    }

    public set variant(value: LabelVariant) {
        this._variant = value;
        this.themeKey = LabelVariantUtil.getThemeKey(value);
    }

    override styleForTooltip() {
        this.linkToListLayout = true;
        if (this.link != null) {
            this.linkOpenInNewTab = true;
        }
    }

    get listLayout(): string {
        return this.resolveLayoutName(this._listLayout ?? this._boundField?.listLayout);
    }

    set listLayout(value: string) {
        this._listLayout = value;
        this.syncLinkToListLayout();
        this.syncContextMenuListeners();
    }

    protected get linkToListLayout(): boolean {
        return this._linkToListLayout ?? false;
    }

    protected set linkToListLayout(value: boolean) {
        this._linkToListLayout = value;
        this.syncLinkToListLayout();
    }

    private syncLinkToListLayout() {
        if (this.canLinkToListLayout ) {
            this.color = null;
        }
        this.syncLinkClickListener();
    }

    private get canLinkToListLayout(): boolean {
        return this.hasNonNullDisplayValue() &&
            this.linkToListLayout &&
            LayoutNameResolver.resolve(this.listLayoutLauncherProps?.listLayout) != null
    }

    public get listLayoutLauncherProps(): ListLayoutLauncherProps {
        if (this.listLayout == null)  return null;

        return {
            listLayout: this.listLayout,
            keyValues: [this.getDataValue()]
        };
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Label, [ QuickInfoMixin, BusyMixin ]);

// TypeScript interface declaration for mixin integration
export interface Label extends QuickInfoMixinInterface, BusyMixinInterface {
}

ComponentTypes.registerComponentType("label", {
    constructor: Label.prototype.constructor
});
